import { apiClient } from './api';

export interface AccountingOverview {
  todays_sales: number;
  todays_sales_change_pct: number;
  todays_collection: number;
  todays_collection_count: number;
  overdue_amount: number;
  overdue_customer_count: number;
  outstanding_receivables: number;
  payment_modes: { method: string; total: number }[];
  expected_cash_inflow: {
    today: number;
    tomorrow: number;
    this_week: number;
    future: number;
  };
}

export interface CustomerDueRow {
  customer_id: number;
  customer_name: string;
  city: string | null;
  state: string | null;
  outstanding: number;
  oldest_due_days: number;
  last_payment_date: string | null;
  sales_rep_name: string | null;
  risk: 'block' | 'watch' | 'safe';
  /** AR aging buckets (VP-ADM-004) — sum of per-invoice outstanding balance by days-past-due. */
  aging: {
    days_0_30: number;
    days_31_60: number;
    days_61_90: number;
    days_90_plus: number;
  };
}

export interface CollectionRow {
  rep_id: number;
  rep_name: string;
  payment_count: number;
  total_amount: number;
  status_breakdown: { status: string; count: number; amount: number }[];
}

export interface LedgerEntry {
  date: string;
  customer_id: number;
  customer_name: string;
  type: 'invoice' | 'payment';
  reference: string;
  description: string;
  debit: number;
  credit: number;
}

export async function getOverview(): Promise<AccountingOverview> {
  return apiClient.get<AccountingOverview>('/accounting/overview');
}

export async function getCustomersDue(limit = 25): Promise<CustomerDueRow[]> {
  const raw = await apiClient.get<any>('/accounting/customers-due', { limit });
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
}

export async function getTodaysCollections(): Promise<CollectionRow[]> {
  const raw = await apiClient.get<any>('/accounting/collections/today');
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
}

export interface LedgerFilters {
  customer_id?: number;
  date_from?: string;
  date_to?: string;
}

export async function getRecentLedger(limit = 50, filters: LedgerFilters = {}): Promise<LedgerEntry[]> {
  const raw = await apiClient.get<any>('/accounting/ledger', { limit, ...filters });
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
}

/**
 * Sec 12 — server-side CSV export (GET /accounting/ledger?format=csv), same
 * filters as the JSON call. Replaces the old client-side "generate a CSV
 * from already-fetched rows" approach so the export reflects a real,
 * independently-filterable server query rather than whatever happens to be
 * in memory.
 */
export async function downloadLedgerCsv(filters: LedgerFilters = {}): Promise<Blob> {
  return apiClient.downloadBlob('/accounting/ledger', { ...filters, format: 'csv' });
}
